import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { collection, onSnapshot, query, orderBy, doc, updateDoc, where } from "firebase/firestore";
import { 
  Search, History, X, Trash2, Users, Clock, CheckCircle, 
  Activity, Calendar, User, Pill, Stethoscope, ChevronRight 
} from "lucide-react"; 
import { useNavigate } from "react-router-dom";

const PatientList = () => {
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    // Daily Filter - Today's Registered Patients
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const q = query(
      collection(db, "hospitals", user.uid, "patients"),
      where("registeredAt", ">=", today),
      orderBy("registeredAt", "asc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allToday = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPatients(allToday); 
    });
    return () => unsubscribe();
  }, []);

  // Stats Logic
  const stats = {
    total: patients.length,
    waiting: patients.filter(p => p.status === "waiting").length,
    consulted: patients.filter(p => p.status === "consulted").length
  };

  // Search Filter
  const filteredPatients = patients.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.phone?.includes(searchTerm)
  );

  // --- DELETE VISIT HISTORY LOGIC ---
  const handleDeleteVisit = async (visitDate) => {
    if (!window.confirm("Are you sure you want to delete this visit record?")) return;

    try {
      const user = auth.currentUser;
      const updatedHistory = selectedPatient.visitHistory.filter(v => v.date !== visitDate);
      
      const patientRef = doc(db, "hospitals", user.uid, "patients", selectedPatient.id);
      await updateDoc(patientRef, { visitHistory: updatedHistory });
      
      // Local state update for immediate UI change
      setSelectedPatient({ ...selectedPatient, visitHistory: updatedHistory });
    } catch (err) {
      alert("Error deleting record: " + err.message);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 bg-[#F8FAFC] min-h-screen font-sans antialiased text-slate-900">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="text-center md:text-left">
          <h1 className="text-2xl font-[900] tracking-tighter text-slate-900 uppercase">
            Active <span className="text-blue-600">Patient List</span>
          </h1>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} • OPD Monitor
          </p>
        </div>
        
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Search by Name or Phone..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl outline-none shadow-sm focus:border-blue-500 transition-all font-bold text-xs"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* STATS COUNTER */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Waiting', val: stats.waiting, color: 'orange', icon: Clock },
          { label: 'Consulted', val: stats.consulted, color: 'emerald', icon: CheckCircle },
          { label: 'Total', val: stats.total, color: 'blue', icon: Users }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-3 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3">
            <div className={`p-2 rounded-lg ${stat.color === 'orange' ? 'bg-orange-50 text-orange-600' : stat.color === 'emerald' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
              <stat.icon size={16} strokeWidth={3} />
            </div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase leading-none mb-1">{stat.label}</p>
              <p className="text-lg font-[900] text-slate-900 leading-none">{stat.val}</p>
            </div>
          </div>
        ))}
      </div>

      {/* PATIENT GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {filteredPatients.map(p => (
          <div key={p.id} className={`bg-white p-3 rounded-xl border transition-all flex items-center gap-4 ${p.status === 'consulted' ? 'bg-slate-50 border-slate-100' : 'border-white shadow-sm hover:shadow-md'}`}>
            <div className={`w-1 h-10 rounded-full ${p.status === 'consulted' ? 'bg-emerald-400' : 'bg-blue-600'}`}></div>
            <div className="flex-1 min-w-0">
              <h3 className={`text-sm font-black uppercase truncate ${p.status === 'consulted' ? 'text-slate-500' : 'text-slate-800'}`}>{p.name}</h3>
              <div className="flex items-center gap-3 text-[10px] font-bold text-slate-500">
                <span className="flex items-center gap-1"><Activity size={10}/> {p.age}Y • {p.gender[0]}</span>
                <span>• {p.phone}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => p.status === 'waiting' ? navigate(`/consultation/${p.id}`, { state: { patient: p } }) : setSelectedPatient(p)}
                className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${p.status === 'waiting' ? 'bg-slate-900 text-white hover:bg-blue-600' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              >
                {p.status === 'waiting' ? 'Consult' : 'View'}
              </button>
              <button onClick={() => setSelectedPatient(p)} className="p-2 text-slate-400 hover:text-blue-600 transition-all">
                <History size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* PATIENT CASE SHEET MODAL (HISTORY VIEW) */}
      {selectedPatient && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div>
                <h2 className="text-2xl font-[900] uppercase tracking-tighter italic">Patient <span className="text-blue-600">Case Sheet</span></h2>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Medical History Records</p>
              </div>
              <button onClick={() => setSelectedPatient(null)} className="p-2 hover:bg-red-50 hover:text-red-600 rounded-full transition-all">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                
                {/* Left: Patient Info Summary */}
                <div className="lg:col-span-4">
                  <div className="bg-blue-50/50 p-6 rounded-[2rem] border border-blue-100 sticky top-0">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-blue-600 rounded-lg text-white"><User size={20}/></div>
                      <p className="text-xs font-black text-blue-600 uppercase tracking-widest">Profile Info</p>
                    </div>
                    <div className="space-y-4">
                      <div><p className="text-[9px] font-black text-slate-400 uppercase">Name</p><p className="text-sm font-black text-slate-800 uppercase">{selectedPatient.name}</p></div>
                      <div><p className="text-[9px] font-black text-slate-400 uppercase">Age/Gender</p><p className="text-sm font-black text-slate-800">{selectedPatient.age} Yrs • {selectedPatient.gender}</p></div>
                      <div><p className="text-[9px] font-black text-slate-400 uppercase">Contact</p><p className="text-sm font-black text-slate-800">{selectedPatient.phone}</p></div>
                    </div>
                  </div>
                </div>

                {/* Right: Detailed Visit Timeline */}
                <div className="lg:col-span-8">
                  <div className="flex items-center gap-2 mb-6">
                    <History size={18} className="text-blue-600" />
                    <h3 className="text-sm font-black uppercase tracking-widest">Visit History</h3>
                  </div>

                  <div className="space-y-6 relative before:absolute before:left-4 before:top-2 before:bottom-0 before:w-0.5 before:bg-slate-100">
                    {selectedPatient.visitHistory && selectedPatient.visitHistory.length > 0 ? (
                      [...selectedPatient.visitHistory].reverse().map((visit, idx) => (
                        <div key={idx} className="relative pl-12 group">
                          {/* Timeline Marker */}
                          <div className="absolute left-0 w-8 h-8 bg-white border-2 border-blue-500 rounded-full flex items-center justify-center z-10">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          </div>

                          <div className="bg-white border border-slate-100 rounded-3xl p-5 shadow-sm hover:border-blue-200 transition-all">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <p className="text-xs font-black text-slate-800 flex items-center gap-2">
                                  <Calendar size={14} className="text-blue-500" />
                                  {new Date(visit.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  <span className="text-slate-400 ml-2 font-bold">{new Date(visit.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                </p>
                              </div>
                              <button 
                                onClick={() => handleDeleteVisit(visit.date)}
                                className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div className="bg-slate-50 p-3 rounded-2xl">
                                <p className="text-[9px] font-black text-slate-400 uppercase mb-1">Clinical Diagnosis</p>
                                <p className="text-xs font-black text-blue-700 uppercase leading-tight">{visit.diagnosis || 'N/A'}</p>
                              </div>
                              <div className="bg-slate-50 p-3 rounded-2xl">
                                <p className="text-[9px] font-black text-slate-400 uppercase mb-1">Chief Complaints</p>
                                <p className="text-xs font-bold text-slate-600 leading-tight">{visit.complaints || 'None'}</p>
                              </div>
                            </div>

                            {/* Medicines Section */}
                            {visit.medicines?.length > 0 && (
                              <div className="border-t border-slate-50 pt-4">
                                <div className="flex flex-wrap gap-2">
                                  {visit.medicines.map((m, i) => (
                                    <div key={i} className="flex flex-col bg-blue-50/30 border border-blue-100/50 px-3 py-2 rounded-xl">
                                      <span className="text-[10px] font-black text-slate-800 uppercase flex items-center gap-1">
                                        <Pill size={10} className="text-blue-500"/> {m.name}
                                      </span>
                                      <span className="text-[9px] font-bold text-blue-500 pl-4">{m.duration} • {m.timing}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-20 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                        <Stethoscope size={40} className="mx-auto text-slate-200 mb-2" />
                        <p className="text-slate-400 font-black uppercase text-[10px] tracking-widest">No Medical History Found</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientList;